type Cleanup = () => void;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Partial<Record<string, string>> = {},
  children: (HTMLElement | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v !== undefined) node.setAttribute(k, v);
  }
  for (const c of children) {
    node.append(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

export function renderShell(root: HTMLElement): HTMLElement {
  root.innerHTML = "";
  const wrap = el("div", { class: "wrap" });
  root.appendChild(wrap);
  return wrap;
}

export function renderLoading(parent: HTMLElement, message: string): void {
  parent.innerHTML = "";
  parent.appendChild(el("div", { class: "state loading" }, [message]));
}

export function renderError(parent: HTMLElement, message: string): void {
  parent.innerHTML = "";
  parent.appendChild(el("div", { class: "state error" }, [message]));
}

export function renderEmpty(parent: HTMLElement, message: string): void {
  parent.innerHTML = "";
  parent.appendChild(el("div", { class: "state empty" }, [message]));
}

export interface SignInHandlers {
  onRequestOtp: (email: string) => Promise<void>;
  onSubmitOtp: (email: string, code: string) => Promise<void>;
}

export function renderSignIn(parent: HTMLElement, h: SignInHandlers): Cleanup {
  parent.innerHTML = "";
  const card = el("div", { class: "card signin" });

  const title = el("h1", {}, ["Bekijk de sessie"]);
  const sub = el(
    "p",
    { class: "muted" },
    ["Vul je e-mailadres in. Je ontvangt direct een 6-cijferige code."]
  );

  const emailInput = el("input", {
    type: "email",
    autocomplete: "email",
    placeholder: "naam@voorbeeld.nl",
    required: "true",
  });

  const codeInput = el("input", {
    type: "text",
    inputmode: "numeric",
    autocomplete: "one-time-code",
    placeholder: "Code uit je e-mail",
    maxlength: "6",
  });
  codeInput.style.display = "none";

  const button = el("button", { type: "submit" }, ["Verstuur code"]);
  const status = el("p", { class: "status", role: "status" });

  let phase: "email" | "code" = "email";

  const form = el("form", { class: "form" }, [
    emailInput,
    codeInput,
    button,
    status,
  ]) as HTMLFormElement;

  function setBusy(on: boolean): void {
    button.toggleAttribute("disabled", on);
  }
  function setStatus(text: string, kind: "info" | "error" = "info"): void {
    status.textContent = text;
    status.className = `status ${kind}`;
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (phase === "email") {
        const email = (emailInput as HTMLInputElement).value.trim();
        if (!email.includes("@")) {
          setStatus("Voer een geldig e-mailadres in.", "error");
          return;
        }
        await h.onRequestOtp(email);
        phase = "code";
        emailInput.setAttribute("disabled", "true");
        codeInput.style.display = "";
        (codeInput as HTMLInputElement).focus();
        button.textContent = "Inloggen";
        setStatus("Code verstuurd naar je e-mail.");
      } else {
        const email = (emailInput as HTMLInputElement).value.trim();
        const code = (codeInput as HTMLInputElement).value.trim();
        if (code.length < 4) {
          setStatus("Voer de code uit je e-mail in.", "error");
          return;
        }
        await h.onSubmitOtp(email, code);
      }
    } catch (err) {
      setStatus(
        err instanceof Error ? err.message : "Onbekende fout, probeer opnieuw.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  });

  card.append(title, sub, form);
  parent.appendChild(card);

  return () => {
    /* no-op cleanup for sign-in form */
  };
}

export function renderCountdown(
  parent: HTMLElement,
  webinarTitle: string,
  startsAt: Date
): Cleanup {
  parent.innerHTML = "";
  const card = el("div", { class: "card countdown" });
  card.append(
    el("h1", {}, [webinarTitle]),
    el("p", { class: "muted" }, ["De sessie begint om:"]),
    el("p", { class: "scheduled" }, [
      startsAt.toLocaleString("nl-NL", {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      }),
    ])
  );
  const counter = el("p", { class: "counter" });
  card.appendChild(counter);
  parent.appendChild(card);

  function tick(): void {
    const diff = startsAt.getTime() - Date.now();
    if (diff <= 0) {
      counter.textContent = "Bijna online…";
      return;
    }
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    counter.textContent = `${days > 0 ? `${days}d ` : ""}${hours
      .toString()
      .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }
  tick();
  const interval = setInterval(tick, 1000);
  return () => clearInterval(interval);
}

export function renderPlayerShell(
  parent: HTMLElement,
  title: string
): HTMLElement {
  parent.innerHTML = "";
  const card = el("div", { class: "card player" });
  card.append(el("h1", {}, [title]));
  const playerHost = el("div", { class: "player-host" });
  card.appendChild(playerHost);
  parent.appendChild(card);
  return playerHost;
}
